import {AfterViewInit, Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {BaseWidgetComponent} from "../common/BaseWidget";
import {UserMetricInstance} from "../../../models/user/Metric/UserMetricInstance";
import {WidgetButton} from "../common/WidgetButton";
import {DashboardService} from "../../../services/dashboard/dashboard.service";
import {DashboardWidget} from "../../../models/dashboard/DashboardWidget";
import {MetricOpeService} from "../../../services/ope/metricOpe.service";
import {GetMetricInstancesCB} from "../../../ope/metric/GetMetricInstancesCB";
import {NotificationService} from "../../../services/notification/notification.service";
import {
  MetricTelemetryNotificationService
} from "../../../services/notification/interfaces/MetricTelemetryNotificationService";
import {UpdateMetricValueNtf} from "../../../events/notification/UpdateMetricValueNtf";
import {Subject} from "rxjs";
import {debounceTime} from "rxjs/operators";

@Component({
  selector: 'app-measure-cards',
  templateUrl: './measure-cards.component.html',
  styleUrls: ['./measure-cards.component.css',]
})
export class MeasureCardsComponent extends BaseWidgetComponent implements OnInit, AfterViewInit, OnDestroy {

  /**
   * Edit mode on/off
   */
  @Input() editMode = false;

  @Input() widgetOptions: any;
  /**
   * remove widget event
   */
  @Output() removeItem = new EventEmitter();

  /**
   * edit widget event
   */
  @Output() editItem = new EventEmitter<any>();
  selectedMetrics: UserMetricInstance[] = [];
  selectedMetricIds: number[] = [];
  cards: UserMetricInstance[] = [];
  popupOpen: boolean = false;
  selectedTabIndex: number = 0;
  searchInput: string = "";

  protected icons = ['flash', 'add_subtract_circle', 'arrow_circle_up', 'battery_charge', 'arrow_down', 'arrow_up', 'arrow_sort', 'arrow_trending_lines', 'power', 'alert', 'prohibited', 'pulse', 'temperature', 'beaker', 'autosum', 'clock', 'gauge', 'gas', 'money', 'stream', 'weather_sunny', 'star', 'drop', 'weather_snowflake'];

  protected metricOpeService = inject(MetricOpeService);


  /**
   * Service containing the shared data and procedures among dashboard components
   * @protected
   */
  protected dashboardService = inject(DashboardService);

  protected metricTelemetryNotificationService: MetricTelemetryNotificationService = inject(NotificationService);

  protected loadingData: boolean = false;

  protected selectedPartition?: any;

  protected subscribedNotificationIds: number[] = [];

  private pendingUpdates = new Map<number, UpdateMetricValueNtf['value']>();
  private flushUpdates$ = new Subject<void>();

  constructor() {
    super();

  }

  ngOnInit() {

    super.ngOnInit();
    let editButton = new WidgetButton();
    editButton.clickable = true;
    editButton.icon = "edit";
    editButton.editOnly = true;
    editButton.callbackFn = () => this.openPupup();
    this.widgetButtons.push(editButton);

    this.subscriptions = [
      this.metricTelemetryNotificationService.onUpdateMetricValue((event: UpdateMetricValueNtf) => {
        this.pendingUpdates.set(event.value.metricId, event.value);
        this.flushUpdates$.next();
      }),
      this.flushUpdates$.pipe(debounceTime(300)).subscribe(() => {
        this.pendingUpdates.forEach((update) => {
          const card = this.cards.find(c => c.id === update.metricId);
          if (card) {
            card.lastValue = update.value;
            card.lastValueTs = update.timestamp;
          }
        });
        this.pendingUpdates.clear();
      })
    ]
  }

  ngAfterViewInit() {
    this.checkWidgetMetricsExistence();
    if (!!this.gridsterItem.userConfig && !!this.gridsterItem.userConfig.cards) {
      let cb = new GetMetricInstancesCB();
      cb.i.metricInstanceIds = [];
      for (let card of this.gridsterItem.userConfig.cards) {
        cb.i.metricInstanceIds.push(card.id);
      }
      this.metricOpeService.getMetricInstances(cb).then(() => {
        this.gridsterItem.userConfig.cards.forEach((mi: UserMetricInstance) => {
          let fullMi = cb.o.metricInstances.find(m => m.id === mi.id);
          if (!!fullMi) {
            mi.lastValue = fullMi.lastValue;
            mi.lastValueTs = fullMi.lastValueTs;
            mi.decimalDigits = fullMi.decimalDigits;
            mi.dataUnit = fullMi.dataUnit;
            mi.deviceName = fullMi.deviceName;
            mi.partitionName = fullMi.partitionName;
            mi.name = fullMi.name;
            mi.wm2eInfo = fullMi.wm2eInfo;
            mi.wm2eStatus = fullMi.wm2eStatus;
            mi.dataTypeGroupShowAsHex = fullMi.dataTypeGroupShowAsHex;
          }
        });

        this.cards = this.gridsterItem.userConfig.cards;
        this.subscribedNotificationIds = this.cards.map(card => card.id);
        this.metricTelemetryNotificationService.addMetricIdsToNotifications(this.subscribedNotificationIds);
      });
    } else {
      this.cards = [];
      this.subscribedNotificationIds = this.cards.map(card => card.id);
      this.metricTelemetryNotificationService.addMetricIdsToNotifications(this.subscribedNotificationIds);
    }
  }

  checkWidgetMetricsExistence(): void {
    if (!!this.gridsterItem.metricInstanceIds) {
      if (!!this.gridsterItem.userConfig) {
        if (!!this.gridsterItem.userConfig.cards) {
          const idsToDelete: number[] = [];
          this.gridsterItem.userConfig.cards.forEach((metric: UserMetricInstance, index: number) => {
            if (!this.gridsterItem.metricInstanceIds.includes(metric.id)) {
              idsToDelete.push(index);
            }
          });
          idsToDelete.sort((a, b) => b - a).forEach((id) => {
            this.gridsterItem.userConfig.cards.splice(id, 1);
          })
        }
      }
    }
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.flushUpdates$.complete();
    this.metricTelemetryNotificationService.removeMetricIdsToNotifications(this.subscribedNotificationIds);
  }

  openPupup() {
    this.popupOpen = true;
  }

  async initializePopupForm($event: any) {
    this.selectedTabIndex = 0;
    this.selectedMetrics = this.appService.deepCopy(this.cards);
  }

  saveCard = () => {
    this.cards = (this.appService.deepCopy(this.selectedMetrics));
    this.metricTelemetryNotificationService.removeMetricIdsToNotifications(this.subscribedNotificationIds);
    this.subscribedNotificationIds = this.cards.map(x => x.id);
    this.metricTelemetryNotificationService.addMetricIdsToNotifications(this.subscribedNotificationIds);
    this.popupOpen = false;
    if (!!this.dashboardService.selectedDashboard) {
      this.gridsterItem.userConfig = {
        cards: this.cards
      };
      this.gridsterItem.metricInstanceIds = this.getMetricInstanceIds();
      this.dashboardService.putDashboardWidget(this.dashboardService.selectedDashboard?.id, DashboardWidget.clone(this.gridsterItem));
    }
  };

  getMetricInstanceIds(): number[] {
    let result: number[] = [];
    this.cards.forEach((metric, index) => {
      if (!result.includes(metric.id)) {
        result.push(metric.id);
      }
    });
    return result
  }

  closeCard = () => {
    this.popupOpen = false;
  };


  reorderItem($event: any) {
    if ($event.toIndex < this.selectedMetrics.length) {
      this.selectedMetrics.splice($event.toIndex, 0, this.selectedMetrics.splice($event.fromIndex, 1)[0]);
    }
  }

  updateSelectedMetrics($event: UserMetricInstance[]) {
    $event[$event.length - 1].color = this.gmService.BASE_COLORS[0];
    $event[$event.length - 1].icon = this.icons[0];
    this.selectedMetrics.push($event[$event.length - 1]);
  }

  deleteCard(index: number) {
    this.selectedMetrics.splice(index, 1);
  }

  trackById(index: number, item: UserMetricInstance) {
    return item.id;
  };

  cardIsSearchedFor(card: UserMetricInstance) {
    if (!this.searchInput || !this.searchInput.replace(/\s/g, '').length) {
      return true;
    }
    if (!!card.deviceName) {
      if (card.partitionName.includes(this.searchInput)
        || card.deviceName.includes(this.searchInput)
        || card.name.includes(this.searchInput)) {
        return true
      }
    } else {
      if (card.partitionName.includes(this.searchInput)
        || card.name.includes(this.searchInput)) {
        return true
      }
    }
    return false;
  }


}
